// Учёт неудачных upload-операций PowerSync + persistent Sync Issues journal.
//
// Сервер отвечает HTTP 200 даже при per-op отказе (invalid / forbidden /
// conflict), поэтому классификация делается здесь, а не по HTTP-коду.
//
// Политика ретраев (почему именно так — см. `connector.ts`):
//   forbidden          — перманентен (CASL_DENIED), ретраить бессмысленно;
//   invalid / conflict  — временны по своей природе (клиент новее сервера до
//                         выката; родительская строка ещё не приехала), поэтому
//                         проходят через счётчик попыток наравне с transient;
//   transient           — сетевые / HTTP ошибки, счётчик попыток.
//
// ── Почему запись журнала хранит СНИМОК полезной нагрузки ──
//
// Локальная строка НЕ переживает отбрасывание операции. Ядро PowerSync на
// каждую локальную запись кладёт строку в `ps_updated_rows` рядом с
// `ps_crud`, а `sync_local` (гейт `SELECT 1 FROM ps_crud LIMIT 1`) на
// ближайшем чекпойнте ре-материализует все такие строки ИЗ `ps_oplog`, то
// есть из серверного состояния. Как только коннектор зовёт `batch.complete()`
// (он же `DELETE FROM ps_crud …` + сброс целевого чекпойнта), гейт открыт —
// и правка пользователя в локальной SQLite затирается серверной версией, а
// для PUT-строки, которой на сервере нет вовсе, строка просто исчезает.
//
// Поэтому снимок снимается ЗДЕСЬ — в момент журналирования, до
// `batch.complete()`, — и повтор шлёт именно его (`sync-recovery.ts`).
// Читать локальную строку при повторе нельзя: там уже данные сервера.
import { createStore, del, get, keys, set } from "idb-keyval";
import { getPowerSync } from "./db-instance.js";

const ATTEMPTS_STORAGE_KEY = "studyhub:sync-attempts:v1";
const JOURNAL_DB = "studyhub-sync-failures";
const JOURNAL_STORE = "failures";
const JOURNAL_KEY_PREFIX = "failure:";
const JOURNAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Сколько раз пытаемся ошибку до журналирования. */
export const MAX_UPLOAD_ATTEMPTS = 5;

export const SYNC_OPS_DROPPED_EVENT = "studyhub:sync-ops-dropped";
export const SYNC_FAILURES_CHANGED_EVENT = "studyhub:sync-failures-changed";

export type SyncFailureKind = "transient" | "permanent" | "conflict";
export type SyncFailureReason = "forbidden" | "invalid" | "conflict" | "dropped";

/**
 * Потолок размера снимка (символов JSON).
 *
 * Снимок нужен, чтобы спасти правку, но не ценой падения записи журнала: если
 * `set()` упрётся в квоту IndexedDB и бросит, коннектор не закроет батч и
 * PowerSync будет крутить его по кругу. Поэтому у гигантской нагрузки снимок
 * не сохраняется — остаётся сама запись о потере плюс `copyText`.
 */
const MAX_PAYLOAD_CHARS = 2_000_000;

export interface DroppedOperation {
	table: string;
	id: string;
	op: "PUT" | "PATCH" | "DELETE";
	status: string;
	attempts: number;
}

export interface SyncFailureRecord {
	id: string;
	ownerId: string;
	table: string;
	entityId: string;
	op: "PUT" | "PATCH" | "DELETE";
	kind: SyncFailureKind;
	reason: SyncFailureReason;
	createdAt: number;
	expiresAt: number;
	entityLabel: string | null;
	copyText: string | null;
	/**
	 * Снимок тела операции на момент отказа (для PUT/PATCH) — ровно то, что
	 * коннектор пытался отправить. `null` — для DELETE (тела нет), для записей,
	 * созданных версией до появления снимков, и для нагрузки сверх
	 * `MAX_PAYLOAD_CHARS`.
	 */
	payload: Record<string, unknown> | null;
}

/**
 * Таблицы, которые клиент читает из PowerSync, но пишет только через tRPC.
 * Локальный INSERT/UPDATE по ним порождает PUT/PATCH без handler'а →
 * `invalid`. Повтор бессмысленен — надо снять запись журнала.
 */
export const READ_ONLY_SYNC_TABLES: ReadonlySet<string> = new Set(["character_sessions"]);

/**
 * Можно ли вообще повторить эту запись.
 *
 * Единственный источник правды для UI и для `retrySyncFailure`: кнопка не
 * должна предлагать действие, которое гарантированно упрётся в «отправлять
 * нечего».
 */
export function canRetrySyncFailure(record: SyncFailureRecord): boolean {
	// CASL-отказ перманентен — повтор ничего не изменит.
	if (record.reason === "forbidden") return false;
	// Download-only таблицы: write-handler'а на сервере нет, повтор всегда
	// `invalid`. Сессия персонажа уже создана tRPC — в журнале только шум.
	if (READ_ONLY_SYNC_TABLES.has(record.table)) return false;
	if (record.op === "DELETE") return true;
	return record.payload != null && Object.keys(record.payload).length > 0;
}

const journalStore = createStore(JOURNAL_DB, JOURNAL_STORE);

/**
 * Ключ записи журнала — детерминированный.
 *
 * Раньше это был `crypto.randomUUID()`, и одна реальная потеря давала до пяти
 * одинаковых строк: при перманентном отказе внутри батча с transient-операцией
 * коннектор бросал исключение до `batch.complete()`, PowerSync повторял тот же
 * батч, и тот же отказ журналировался заново. Дедупа не было.
 */
function journalKey(input: {
	ownerId: string;
	table: string;
	entityId: string;
	op: string;
}): string {
	return `${JOURNAL_KEY_PREFIX}${input.ownerId}:${input.table}:${input.entityId}:${input.op}`;
}

function readAllAttempts(): Record<string, number> {
	try {
		const raw = localStorage.getItem(ATTEMPTS_STORAGE_KEY);
		return raw ? (JSON.parse(raw) as Record<string, number>) : {};
	} catch {
		return {};
	}
}

function writeAllAttempts(map: Record<string, number>): void {
	try {
		localStorage.setItem(ATTEMPTS_STORAGE_KEY, JSON.stringify(map));
	} catch {
		// приватный режим / квота
	}
}

export function classifyWriteStatus(status: string): {
	kind: SyncFailureKind;
	reason: SyncFailureReason;
} {
	if (status === "conflict") return { kind: "conflict", reason: "conflict" };
	if (status === "forbidden") return { kind: "permanent", reason: "forbidden" };
	if (status === "invalid") return { kind: "permanent", reason: "invalid" };
	return { kind: "transient", reason: "dropped" };
}

export function bumpAttempt(key: string): number {
	const map = readAllAttempts();
	const next = (map[key] ?? 0) + 1;
	map[key] = next;
	writeAllAttempts(map);
	return next;
}

export function clearAttempts(keysToClear: readonly string[]): void {
	if (keysToClear.length === 0) return;
	const map = readAllAttempts();
	let changed = false;
	for (const key of keysToClear) {
		if (key in map) {
			delete map[key];
			changed = true;
		}
	}
	if (changed) writeAllAttempts(map);
}

export function clearAllAttempts(): void {
	try {
		localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
	} catch {
		// no-op
	}
}

function notifyFailuresChanged(): void {
	if (typeof window === "undefined") return;
	window.dispatchEvent(new CustomEvent(SYNC_FAILURES_CHANGED_EVENT));
}

export function notifyDropped(operations: readonly DroppedOperation[]): void {
	if (operations.length === 0 || typeof window === "undefined") return;
	window.dispatchEvent(
		new CustomEvent<DroppedOperation[]>(SYNC_OPS_DROPPED_EVENT, {
			detail: [...operations],
		}),
	);
}

/**
 * Колонки подписи и «спасаемого текста» для таблиц, которые мы знаем в лицо.
 * Для остальных берутся общие имена — журнал пишется для ЛЮБОЙ синкаемой
 * таблицы, и «Элемент 0190a000» вместо названия события календаря бесполезен.
 */
const META_COLUMNS: Record<string, { label: readonly string[]; copy: readonly string[] }> = {
	notes: { label: ["title"], copy: ["content_markdown", "plain_text"] },
	materials: { label: ["name"], copy: ["notes"] },
	flashcard_sets: { label: ["name"], copy: ["description"] },
	character_sessions: { label: ["character_title"], copy: [] },
};

const GENERIC_LABEL_COLUMNS = ["title", "name", "question", "term"] as const;
const GENERIC_COPY_COLUMNS = [
	"content_markdown",
	"plain_text",
	"description",
	"notes",
	"answer",
	"text",
] as const;

/** Первое непустое строковое значение по списку колонок. */
function pickText(
	source: Record<string, unknown> | null | undefined,
	columns: readonly string[],
): string | null {
	if (!source) return null;
	for (const column of columns) {
		const value = source[column];
		if (typeof value === "string" && value.trim()) return value.trim();
	}
	return null;
}

async function readLocalMeta(
	table: string,
	columns: readonly string[],
	entityId: string,
): Promise<Record<string, unknown> | null> {
	if (columns.length === 0) return null;
	try {
		const row = await getPowerSync().getOptional<Record<string, unknown>>(
			`SELECT ${columns.join(", ")} FROM ${table} WHERE id = ? LIMIT 1`,
			[entityId],
		);
		return row ?? null;
	} catch {
		// локальная БД может быть недоступна / таблицы нет в схеме клиента
		return null;
	}
}

/**
 * Подпись строки для карточки в журнале + текст, который пользователь может
 * унести в буфер обмена.
 *
 * Снимок операции имеет приоритет над локальной строкой: именно он содержит то,
 * что пользователь ввёл и что не уехало. Локальная строка — фолбэк для колонок,
 * которых не было в диффе PATCH.
 */
async function resolveEntityMeta(
	table: string,
	entityId: string,
	payload: Record<string, unknown> | null,
): Promise<{ label: string | null; copyText: string | null }> {
	const spec = META_COLUMNS[table];
	const labelColumns = spec?.label ?? GENERIC_LABEL_COLUMNS;
	const copyColumns = spec?.copy ?? GENERIC_COPY_COLUMNS;
	const row = spec ? await readLocalMeta(table, [...labelColumns, ...copyColumns], entityId) : null;

	const label = pickText(payload, labelColumns) ?? pickText(row, labelColumns);
	const copyText = pickText(payload, copyColumns) ?? pickText(row, copyColumns) ?? label;
	return { label, copyText };
}

/**
 * Снимок для записи журнала.
 *
 * PATCH у PowerSync — это ДИФФ строки, поэтому несколько подряд отказавших
 * правок одной сущности склеиваются по колонкам: иначе последний дифф
 * (например `{title}`) затёр бы в журнале ранее потерянный `{content_markdown}`.
 * PUT несёт строку целиком — там побеждает последний.
 */
function mergePayload(
	op: "PUT" | "PATCH" | "DELETE",
	previous: Record<string, unknown> | null | undefined,
	next: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
	if (op === "DELETE") return null;
	if (!next || Object.keys(next).length === 0) return previous ?? null;
	const merged = op === "PATCH" && previous ? { ...previous, ...next } : next;
	if (JSON.stringify(merged).length > MAX_PAYLOAD_CHARS) return null;
	return merged;
}

export async function journalSyncFailure(input: {
	ownerId: string;
	table: string;
	entityId: string;
	op: "PUT" | "PATCH" | "DELETE";
	kind: SyncFailureKind;
	reason: SyncFailureReason;
	/** Тело операции, которую сервер не принял (для PUT/PATCH). */
	payload?: Record<string, unknown> | null;
}): Promise<SyncFailureRecord> {
	const now = Date.now();
	const key = journalKey(input);
	// Upsert по детерминированному ключу: повтор того же отказа обновляет
	// существующую строку, а не плодит соседнюю (см. `journalKey`).
	const previous = await get<SyncFailureRecord>(key, journalStore);
	const payload = mergePayload(input.op, previous?.payload, input.payload);
	const meta = await resolveEntityMeta(input.table, input.entityId, payload);
	const record: SyncFailureRecord = {
		id: key,
		ownerId: input.ownerId,
		table: input.table,
		entityId: input.entityId,
		op: input.op,
		kind: input.kind,
		reason: input.reason,
		createdAt: previous?.createdAt ?? now,
		expiresAt: now + JOURNAL_TTL_MS,
		entityLabel: meta.label,
		copyText: meta.copyText,
		payload,
	};
	await writeRecord(key, record);
	notifyFailuresChanged();
	return record;
}

/**
 * Запись в журнал с деградацией вместо исключения.
 *
 * Снимок может не влезть в квоту IndexedDB. Потерять из-за этого САМУ запись о
 * потере — худший исход (коннектор бы ещё и не закрыл батч), поэтому при
 * ошибке пробуем сохранить ту же запись без снимка.
 */
async function writeRecord(key: string, record: SyncFailureRecord): Promise<void> {
	try {
		await set(key, record, journalStore);
	} catch {
		await set(key, { ...record, payload: null }, journalStore);
	}
}

async function pruneExpiredFailures(): Promise<void> {
	const now = Date.now();
	const allKeys = await keys(journalStore);
	let changed = false;
	for (const key of allKeys) {
		if (typeof key !== "string" || !key.startsWith(JOURNAL_KEY_PREFIX)) continue;
		const record = await get<SyncFailureRecord>(key, journalStore);
		if (!record || record.expiresAt <= now) {
			await del(key, journalStore);
			changed = true;
		}
	}
	if (changed) notifyFailuresChanged();
}

export async function listSyncFailures(ownerId: string): Promise<SyncFailureRecord[]> {
	if (!ownerId) return [];
	await pruneExpiredFailures();
	const allKeys = await keys(journalStore);
	const out: SyncFailureRecord[] = [];
	for (const key of allKeys) {
		if (typeof key !== "string" || !key.startsWith(JOURNAL_KEY_PREFIX)) continue;
		const record = await get<SyncFailureRecord>(key, journalStore);
		if (record && record.ownerId === ownerId) out.push(record);
	}
	return out.toSorted((a, b) => b.createdAt - a.createdAt);
}

export async function removeSyncFailure(id: string): Promise<void> {
	// `id` записи и есть ключ в хранилище (см. `journalSyncFailure`). Старые
	// записи с UUID-идентификатором лежат под `failure:<uuid>` — удаляем обе
	// формы, чтобы журнал не зарастал после апгрейда.
	await del(id, journalStore);
	await del(`${JOURNAL_KEY_PREFIX}${id}`, journalStore);
	notifyFailuresChanged();
}

export async function clearSyncFailuresForOwner(ownerId: string): Promise<void> {
	if (!ownerId) return;
	const allKeys = await keys(journalStore);
	for (const key of allKeys) {
		if (typeof key !== "string" || !key.startsWith(JOURNAL_KEY_PREFIX)) continue;
		const record = await get<SyncFailureRecord>(key, journalStore);
		if (record?.ownerId === ownerId) await del(key, journalStore);
	}
	notifyFailuresChanged();
}

export function subscribeSyncFailures(listener: () => void): () => void {
	if (typeof window === "undefined") return () => {};
	const handler = (): void => listener();
	window.addEventListener(SYNC_FAILURES_CHANGED_EVENT, handler);
	return () => window.removeEventListener(SYNC_FAILURES_CHANGED_EVENT, handler);
}
